import { KeyboardEvent, useEffect, useMemo, useRef } from "react";
import { TScript } from "pages/media/types";
import styled from "styled-components";
import { useVolatileBoundStore } from "store";
import { GlobalToken } from "antd";
import {
  registerHighlightsRef,
  scrollToHighlight,
} from "../../animation/helpers/helpers";
import {
  parseScript,
  serializeScript,
  getAnchorRanges,
} from "pages/media/helpers/scriptAnchors";
import { nanoid } from "nanoid";
import { useHighlightsStore } from "../../animator/useHighlightsStore";
import { domToParsedScript, renderRangeAsHtml } from "./helpers";

const spanWithHighlightClass = "highlight-tag";

export const ScriptWithHighlights = ({
  script,
  contentEditable = true,
  index,
  onEditStart,
  onEditComplete,
}: {
  script: string;
  onEditComplete?: (index: number, script: Partial<TScript>) => void;
  contentEditable?: boolean;
  index: number;
  onEditStart?: () => void;
}) => {
  const editableContainerRef = useRef<HTMLDivElement>(null);

  const { setHighlightIdToScroll, highlightRanks } = useVolatileBoundStore(
    (state) => ({
      setHighlightIdToScroll: state.setHighlightIdToScroll,
      highlightRanks: state.storyboardState.highlightRanks,
    })
  );

  const {
    highlights,
    sectionHighlights,
    setCurrentHighlight,
    currentHighlight,
  } = useHighlightsStore();

  const parsed = useMemo(() => parseScript(script), [script]);

  const chunks = useMemo(() => {
    const hlRanges = getAnchorRanges(parsed, "HL");
    // Build Chunk-like objects from HL ranges + surrounding text
    const result: Array<{
      text: string;
      isHighlighted: boolean;
      id: string | null;
      incomplete?: "start" | "end";
      rangeStart: number;
      rangeEnd: number;
    }> = [];
    let lastEnd = 0;

    for (const range of hlRanges) {
      if (range.start > lastEnd) {
        result.push({
          text: parsed.text.slice(lastEnd, range.start),
          isHighlighted: false,
          id: null,
          rangeStart: lastEnd,
          rangeEnd: range.start,
        });
      }
      result.push({
        text: range.text,
        isHighlighted: true,
        id: range.id,
        incomplete: range.orphan,
        rangeStart: range.start,
        rangeEnd: range.end,
      });
      lastEnd = range.end;
    }
    if (lastEnd < parsed.text.length || lastEnd === 0) {
      result.push({
        text: parsed.text.slice(lastEnd),
        isHighlighted: false,
        id: null,
        rangeStart: lastEnd,
        // +1 so markers at exactly text.length are included
        // (renderRangeAsHtml uses exclusive end)
        rangeEnd: parsed.text.length + 1,
      });
    }
    return result;
  }, [parsed]);

  const chunksWithRank = useMemo(() => {
    return chunks.map((chunk) => {
      const rank = sectionHighlights?.find((sh) => sh.id === chunk.id)?.rank;
      return { ...chunk, rank };
    });
  }, [chunks, sectionHighlights]);

  const scriptChunksArray = useMemo(() => {
    if (!highlightRanks) return chunksWithRank;
    return chunksWithRank?.map((chunk) => {
      if (chunk.rank === undefined || chunk.rank === null) return chunk;
      if (
        highlightRanks.includes(chunk.rank) ||
        (highlightRanks.includes(5) && chunk.rank > 5)
      ) {
        return {
          ...chunk,
          isHighlighted: true,
        };
      } else {
        return {
          ...chunk,
          isHighlighted: false,
        };
      }
    });
  }, [chunksWithRank, highlightRanks]);

  const highlightsRef = useRef<Record<string, HTMLElement>>({});

  useEffect(() => {
    if (currentHighlight) {
      scrollToHighlight(highlightsRef, currentHighlight.id);
    }
  }, [currentHighlight]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!contentEditable) return;
    if (e.key === "Enter") {
      e.preventDefault();
      editableContainerRef.current?.blur();
    }
  };

  const handleOnEnter = () => {
    if (!editableContainerRef.current) return;
    const domParsed = domToParsedScript(editableContainerRef.current);
    const content = serializeScript(domParsed);
    // Only update and reset audio if content actually changed
    if (content !== script) {
      onEditComplete?.(index, { content });
    }
  };

  const wrapperKey = useMemo(() => nanoid(), [script, index]);

  return (
    <HighlightsWrapper
      // hack to avoid react crash with contentEditable and refs
      // https://stackoverflow.com/questions/42956884/failed-to-execute-removechild-on-node
      key={wrapperKey}
      contentEditable={contentEditable ? "plaintext-only" : false}
      suppressContentEditableWarning
      ref={editableContainerRef}
      onKeyDown={handleKeyDown}
      onBlur={contentEditable ? handleOnEnter : () => {}}
      onFocus={contentEditable ? onEditStart : undefined}
    >
      {scriptChunksArray?.map((item, idx) => {
        if (item.isHighlighted) {
          const currentSectionHighlight = sectionHighlights.find(
            (sectionHighlight) => sectionHighlight?.id === item.id
          );
          const relatedHighlight = highlights?.find(
            (h) => h.id === currentSectionHighlight?.sourceId
          );

          return (
            <Highlight
              className={spanWithHighlightClass}
              key={idx}
              id={item.id ?? undefined}
              data-incomplete={item.incomplete}
              ref={(el) =>
                // avoid side effects of contentEditable with refs and node
                // manipulation
                !contentEditable &&
                relatedHighlight?.id &&
                el &&
                registerHighlightsRef(highlightsRef, relatedHighlight.id, el)
              }
              onClick={() => {
                if (contentEditable) return;
                setCurrentHighlight(relatedHighlight);
                if (!relatedHighlight?.id) return;
                setHighlightIdToScroll(relatedHighlight.id);
              }}
              $isCurrent={relatedHighlight?.id === currentHighlight?.id}
              dangerouslySetInnerHTML={{
                __html: renderRangeAsHtml(
                  parsed,
                  item.rangeStart,
                  item.rangeEnd
                ),
              }}
            />
          );
        }
        return (
          <span
            key={idx}
            dangerouslySetInnerHTML={{
              __html: renderRangeAsHtml(parsed, item.rangeStart, item.rangeEnd),
            }}
          />
        );
      })}
    </HighlightsWrapper>
  );
};

const HighlightsWrapper = styled.div<{ theme: GlobalToken }>`
  width: 100%;
  margin: 5px;
  margin-bottom: 20px;
  box-sizing: border-box;
  white-space: pre-wrap;

  /* Remove default browser outline for contentEditable */
  outline: none;

  &[contenteditable="plaintext-only"]:focus {
    outline: 2px solid ${(props) => props.theme.colorPrimary};
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* Ensure nested elements don't interfere with the border */
  &[contenteditable="plaintext-only"]:focus * {
    outline: none !important;
  }
`;

const Highlight = styled.span<{ theme: GlobalToken; $isCurrent: boolean }>`
  transition: 0.3s;
  background-color: ${(props) =>
    `${props.$isCurrent ? props.theme.colorPrimary : props.theme.colorTextDescription}70`};
  cursor: pointer;

  /* to not scroll less than 50px — https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView#controlling_topbottom_alignment */
  scroll-margin-top: 50px;
  scroll-margin-bottom: 50px;

  &:hover {
    background-color: ${(props) =>
      `${props.$isCurrent ? props.theme.colorPrimary : props.theme.colorTextDescription}40`};
  }
`;
